import axios from 'axios'
import { sameOriginUsersApiUrl } from '../../app/axios/const'

// E8 8.1.3 / WI 565 — the pre-login branding the LoginPage hydrates from. Public-safe subset only
// (display name + palette + logo); colours are 6-char hex WITHOUT '#'. logoUrl is null until a Tenant
// uploads a logo (Phase 2), in which case the page shows the default LogikSpark asset.
export interface PublicBranding {
  tenantDisplayName: string
  logoUrl: string | null
  primaryColor: string
  secondaryColor: string
  textColor: string
  contrastTextColor: string
}

// Dedicated ANONYMOUS axios instance: the app's normal instances attach an MSAL bearer token (and throw
// with no signed-in account), so they're unusable on the pre-login page. Base URL already includes /api,
// so the route is relative. Mirrors features/Signup/signupApi.ts.
// Uses the SAME-ORIGIN users base (not the fixed app-host base): /v1/branding resolves the Tenant from
// the request subdomain (X-Forwarded-Host), so it must be called on the tenant's own origin — see
// sameOriginUsersApiUrl. Otherwise it 404s on the wrong subdomain and the LoginPage silently falls back
// to defaultBranding.
const usersAnon = axios.create({
  baseURL: sameOriginUsersApiUrl(),
  headers: { 'Content-Type': 'application/json' },
})

// The Tenant is resolved server-side from the request subdomain — no parameters. Response is the
// ResponseDto envelope ({ data: <payload> }), unwrapped via .data.data like the rest of the app.
export const getPublicBranding = async (signal?: AbortSignal): Promise<PublicBranding> =>
  (await usersAnon.get('/v1/branding', { signal })).data.data

// LogikSpark default palette (mirrors the DefaultBranding seed: 303091 / A0A0CC / 000000 / F1F2FF). Used
// when the branding fetch is slow (500ms ceiling), fails, or 404s on an unknown subdomain.
export const defaultBranding: PublicBranding = {
  tenantDisplayName: '',
  logoUrl: null,
  primaryColor: '303091',
  secondaryColor: 'A0A0CC',
  textColor: '000000',
  contrastTextColor: 'F1F2FF',
}
